// BackendPageForDataDeletion.js — WorksForYou Data Deletion Instructions.
//
// Required by Meta for apps that access user data through the Meta Platform
// (Facebook/Instagram APIs): the app settings must point to a public URL with
// instructions on how users can request deletion of their data. This page is
// that URL (worksforyou.org/data-deletion). Linked from the footer small print.

import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { ConfigContext } from '../../App';
import { Navigation, Footer } from '../../myReactLib/MainBlocks';

const LAST_UPDATED = 'June 12, 2026';

function BackendPageForDataDeletion() {
  const { config, homepageData, contactData } = useContext(ConfigContext);
  if (!homepageData) return null;

  return (
    <div className="w4u-page">
      <Navigation menuBar={homepageData.menu_bar} prefix={config.PREFIX} />
      <main className="w4u-main">
        <article className="w4u-legal">
          <header className="w4u-legal__header">
            <h1 className="w4u-legal__title">Data Deletion Instructions</h1>
            <p className="w4u-legal__date">Last updated: {LAST_UPDATED}</p>
          </header>

          <section className="w4u-legal__section">
            <h2>1. What this page is for</h2>
            <p>
              This page explains how to request the deletion of any personal data held by WorksForYou
              (operated by Eugenio Ruggeri, Bologna, Italy) — including data obtained through
              <strong> Meta Platforms (Facebook and Instagram)</strong> when you or your business
              authorized one of our tools to access your accounts. It complements our{' '}
              <Link to="/privacy" className="w4u-legal__link">Privacy Policy</Link>.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>2. What data we may hold</h2>
            <p>
              <strong>Website contacts:</strong> if you contacted us through this Site, we hold the
              name, email address, and message you sent us.
            </p>
            <p>
              <strong>Connected social accounts (Meta):</strong> if you authorized one of our tools to
              access your Facebook or Instagram account through Meta's official APIs, we may hold the
              data retrieved within the permissions you granted — typically profile information, posts
              and media metadata, comments, and account/page insights — used solely to provide the
              agreed service.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>3. How to request deletion</h2>
            <p>
              Send an email to{' '}
              <a href="mailto:worksforyou.solutions@gmail.com" className="w4u-legal__link">worksforyou.solutions@gmail.com</a>{' '}
              with the subject line <strong>"Data Deletion Request"</strong>, preferably from the email
              address (or mentioning the Facebook/Instagram account) the data relates to. No reason or
              justification is needed.
            </p>
            <p>
              For data from connected Meta accounts, you can also revoke our access directly: on
              Facebook go to <strong>Settings &amp; Privacy → Settings → Apps and Websites</strong> (on
              Instagram: <strong>Settings → Website permissions → Apps and Websites</strong>), remove the
              WorksForYou app, and Meta stops all further access immediately. Then email us (as above)
              to have the already-retrieved data deleted as well.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>4. What happens next</h2>
            <p>
              We delete the requested data from our systems and backups within <strong>30 days</strong>{' '}
              and send you a confirmation email when the deletion is complete. If any data must be
              retained to comply with a legal obligation (e.g. invoicing records), we will tell you
              exactly what is kept and why.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>Contact</h2>
            <p>
              Questions? Email <a href="mailto:worksforyou.solutions@gmail.com" className="w4u-legal__link">worksforyou.solutions@gmail.com</a>.
            </p>
          </section>
        </article>
      </main>
      <Footer menuBar={homepageData.menu_bar} config={config} contactData={contactData} footer={homepageData.footer} />
    </div>
  );
}

export default BackendPageForDataDeletion;
